import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

// Components
import HistorySubmissionStudent from './studentTask/HistorySubmissionStudent';
import StudentTaskHeader from './studentTask/StudentTaskHeader';
import CardCorrectTask from './studentTask/CardCorrectTask';
import CommentsSection from './studentTask/CommentsSection';
import CommentsChat from '../../../components/shared/presentation-comments/CommentsChat';
import InfoOldSection from './studentTask/InfoOldSection';
import SimpleLoading from '../../../components/common/SimpleLoading';
import { Text } from '../../../components/common/Texts';
import Card from '@mui/material/Card';

// Redux
import { useSelector } from 'react-redux';
import {
    setTaskByStudentEdit,
    setTaskByStudentWithFiles,
} from '../../../redux/api/teacher';
import {
    searchConversations,
    getConversationResource,
    postCommentResource,
    postCommentAndCreateConversationResource,
    editCommentResource,
} from '../../../redux/api/comments';
import toast from 'react-hot-toast';

// Utils
import {
    formatCommentDate,
    formatCommentDateTime,
    getCommentSortMs,
} from '../../../utils/dates/dates.utils';
import {
    buildLegacyCommentsFromSubmission,
    mergeLegacyAndConversationComments,
    isAdiProfessorEmail,
} from '../../../components/shared/presentation-comments/legacyComments.utils';

// Utils
import { optionsStyle } from '../../../utils/snackStyles';
import { useSnackbar } from 'react-simple-snackbar';

// Hooks
import { useQueryParams } from '../../../hooks/useQueryParams';

const StudentTask = (props) => {
    const {
        idTask,
        idAssignment,
        state,
        valuesCorrection,
        isRedo,
        activeRol,
        history,
        createDate,
        correctionDate,
        studentComment,
        student,
        professor,
        isFile,
        correctionType,
        order,
        teacherFiles,
        grade,
        resourceId,
        studentId,
        subjectId,
        learningObject,
        submissionId,
        programId,
    } = props;

    const { user } = useSelector((state) => state.auth);
    const [openSnackbar] = useSnackbar(optionsStyle);
    const params = useQueryParams();
    const old = params.has('old');

    const correctionWithCheck = correctionType === 'check_entregado';

    // STATES
    const [note, setNote] = useState(0);
    const [comment, setComment] = useState('');
    const [redo, setRedo] = useState(false);
    const [check, setCheck] = useState(false);
    const [page, setPage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [editorMode, setEditorMode] = useState('idle');
    const [savedCorrection, setSavedCorrection] = useState({
        note: 0,
        redo: false,
        check: false,
        comment: '',
    });
    const [sound, setSound] = useState(null);
    const [video, setVideo] = useState(null);
    const [attachmentFile, setAttachmentFile] = useState(null);

    const [conversationId, setConversationId] = useState(null);
    const [comments, setComments] = useState([]);
    const [commentChat, setCommentChat] = useState('');
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [isEditingMessage, setIsEditingMessage] = useState(false);
    const [chatMediaReset, setChatMediaReset] = useState(0);
    const [chatFile, setChatFile] = useState(null);
    const [chatAudio, setChatAudio] = useState(null);
    const [chatVideo, setChatVideo] = useState(null);
    const [commentsLoading, setCommentsLoading] = useState(false);

    const convertToLocalTime = (date, timezone) => {
        // Convierte la fecha y hora del evento a la zona horaria especificada
        const eventTime = moment.tz(date, timezone);

        // Convierte la hora del evento a la hora local
        const localTime = eventTime.clone().tz(moment.tz.guess());

        return localTime.format('DD/MM/YYYY HH:mm:ss');
    };

    const formatDate = convertToLocalTime(createDate, 'UTC');
    const formatCorrectedDate = convertToLocalTime(correctionDate, 'UTC');
    const hasPersistedCorrection =
        state === 'corregido' &&
        (Boolean(correctionDate) ||
            Boolean(valuesCorrection?.comment_teacher?.trim()) ||
            Boolean(grade) ||
            Boolean(isRedo));
    const legacyComments = useMemo(
        () =>
            buildLegacyCommentsFromSubmission({
                submission: {
                    id: submissionId || idTask,
                    create_date: createDate,
                    date_of_correction: correctionDate,
                    student_comment: studentComment,
                    professor_comment: valuesCorrection?.comment_teacher,
                },
                student,
                professor,
            }),
        [
            correctionDate,
            createDate,
            idTask,
            professor,
            student,
            studentComment,
            submissionId,
            valuesCorrection?.comment_teacher,
        ]
    );

    const mergeChatOptions = useMemo(
        () => ({
            submissionId: submissionId || idTask,
            isAdiProfessor: isAdiProfessorEmail(professor?.email),
        }),
        [idTask, professor?.email, submissionId]
    );

    // USE EFFECTS
    // NO DEJAR QUE LA NOTA SE MODIFIQUE CON FLECHAS Y SCROLL
    useEffect(() => {
        const handleWheel = (event) => {
            if (document.activeElement.type === 'number') {
                document.activeElement.blur();
            }
        };

        const handleKeyDown = (event) => {
            if (
                document.activeElement.type === 'number' &&
                (event.key === 'ArrowUp' || event.key === 'ArrowDown')
            ) {
                event.preventDefault();
            }
        };

        document.addEventListener('wheel', handleWheel);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('wheel', handleWheel);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const pickAttachmentUrl = (v) =>
        v && typeof v === 'object' && v.url ? v.url : null;

    const mapConversationMessages = (messages = []) =>
        messages.map((item) => {
            const att = item.attachments || {};
            const fileUrl =
                pickAttachmentUrl(att.file) ||
                (item.attachment &&
                item.attachment !== false &&
                item.attachment.url
                    ? item.attachment.url
                    : null);
            const submissionIdNum = Number(item.submission_id);
            return {
                id: item.id,
                name: item.author_name,
                comment: item.message,
                date:
                    formatCommentDate(item.create_date) +
                    ' ' +
                    formatCommentDateTime(item.create_date),
                photo: item.author_picture || null,
                sender: item.author_is_teacher ? 'user' : 'other',
                author_is_teacher: item.author_is_teacher,
                comment_type: item.comment_type || 'normal',
                submission_id: Number.isNaN(submissionIdNum)
                    ? null
                    : submissionIdNum,
                sort_ms: getCommentSortMs(item.create_date),
                image: fileUrl,
                attachments: {
                    fileUrl,
                    audioUrl: pickAttachmentUrl(att.audio),
                    videoUrl: pickAttachmentUrl(att.video),
                },
            };
        });

    const loadConversation = useCallback(async () => {
        if (!resourceId || !studentId) {
            setComments(
                mergeLegacyAndConversationComments(
                    legacyComments,
                    [],
                    mergeChatOptions
                )
            );
            return;
        }
        setCommentsLoading(true);
        try {
            const response = await searchConversations(
                studentId,
                resourceId,
                learningObject,
                subjectId
            );
            if (response && !response.error) {
                if (response.messages && Array.isArray(response.messages)) {
                    setConversationId(response.id || null);
                    setComments(
                        mergeLegacyAndConversationComments(
                            legacyComments,
                            mapConversationMessages(response.messages),
                            mergeChatOptions
                        )
                    );
                } else if (response.conversation) {
                    setConversationId(response.conversation.id);
                    setComments(
                        mergeLegacyAndConversationComments(
                            legacyComments,
                            mapConversationMessages(
                                response.conversation.messages || []
                            ),
                            mergeChatOptions
                        )
                    );
                } else if (
                    response.conversations &&
                    Array.isArray(response.conversations) &&
                    response.conversations.length > 0
                ) {
                    const conv = response.conversations[0];
                    setConversationId(conv.id);
                    if (conv.messages && conv.messages.length > 0) {
                        setComments(
                            mergeLegacyAndConversationComments(
                                legacyComments,
                                mapConversationMessages(conv.messages),
                                mergeChatOptions
                            )
                        );
                    } else {
                        const details = await getConversationResource(conv.id);
                        if (details && !details.error && details.messages) {
                            setComments(
                                mergeLegacyAndConversationComments(
                                    legacyComments,
                                    mapConversationMessages(details.messages),
                                    mergeChatOptions
                                )
                            );
                        } else {
                            setComments(
                                mergeLegacyAndConversationComments(
                                    legacyComments,
                                    [],
                                    mergeChatOptions
                                )
                            );
                        }
                    }
                } else {
                    setConversationId(null);
                    setComments(
                        mergeLegacyAndConversationComments(
                            legacyComments,
                            [],
                            mergeChatOptions
                        )
                    );
                }
            } else {
                setConversationId(null);
                setComments(
                    mergeLegacyAndConversationComments(
                        legacyComments,
                        [],
                        mergeChatOptions
                    )
                );
            }
        } catch (error) {
            setConversationId(null);
            setComments(
                mergeLegacyAndConversationComments(
                    legacyComments,
                    [],
                    mergeChatOptions
                )
            );
        } finally {
            setCommentsLoading(false);
        }
    }, [
        learningObject,
        legacyComments,
        mergeChatOptions,
        resourceId,
        studentId,
        subjectId,
    ]);

    useEffect(() => {
        loadConversation();
    }, [loadConversation, old]);

    useEffect(() => {
        const nextNote = correctionWithCheck
            ? hasPersistedCorrection
                ? 100
                : 0
            : hasPersistedCorrection
              ? Number(grade || 0)
              : 0;
        const nextRedo = Boolean(isRedo);
        const nextCheck = correctionWithCheck ? hasPersistedCorrection : false;
        const nextComment = valuesCorrection?.comment_teacher || '';

        setNote(nextNote);
        setRedo(nextRedo);
        setCheck(nextCheck);
        setComment(nextComment);
        setSavedCorrection({
            note: nextNote,
            redo: nextRedo,
            check: nextCheck,
            comment: nextComment,
        });
        setCommentChat('');
        setEditorMode(hasPersistedCorrection ? 'idle' : 'correction');
        setPage(false);
        setSound(null);
        setVideo(null);
        setAttachmentFile(null);
    }, [
        hasPersistedCorrection,
        correctionWithCheck,
        grade,
        isRedo,
        valuesCorrection?.comment_teacher,
        idTask,
    ]);

    const handleChatSendFile = (file) => {
        setChatFile(file || null);
    };

    const handleChatSendAudio = (blob) => {
        setChatAudio(blob || null);
    };

    const handleChatSendVideo = (blob) => {
        setChatVideo(blob || null);
    };

    const clearChatMediaDraft = () => {
        setChatFile(null);
        setChatAudio(null);
        setChatVideo(null);
        setChatMediaReset((n) => n + 1);
    };

    const handleSendComment = async (e, customMessage = null) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const messageToSend =
            typeof customMessage === 'string' ? customMessage : commentChat;
        if (!messageToSend.trim()) return;

        setIsSendingMessage(true);
        try {
            const formData = new FormData();
            formData.append('message', messageToSend);
            formData.append('teacher_id', String(user?.repo_id ?? ''));
            formData.append('student_id', String(studentId ?? ''));
            formData.append('resource_id', String(resourceId ?? ''));
            formData.append('subject_id', String(subjectId ?? ''));
            formData.append('learning_object_id', String(learningObject ?? ''));

            if (chatFile instanceof File) {
                formData.append('attachment', chatFile);
            }
            if (chatAudio instanceof Blob) {
                formData.append(
                    'file_audio',
                    chatAudio,
                    chatAudio instanceof File
                        ? chatAudio.name
                        : 'audio_grabado.wav'
                );
            }
            if (chatVideo instanceof Blob) {
                formData.append(
                    'file_video',
                    chatVideo,
                    chatVideo instanceof File
                        ? chatVideo.name
                        : 'video_grabado.webm'
                );
            }

            let response;
            if (conversationId) {
                response = await postCommentResource(conversationId, formData);
            } else {
                response = await postCommentAndCreateConversationResource(
                    resourceId,
                    formData
                );
            }

            if (
                response &&
                !response.error &&
                (response.id != null || response.message_id != null)
            ) {
                const resAtt = response.attachments || {};
                const fileUrl =
                    pickAttachmentUrl(resAtt.file) ||
                    (response.attachment &&
                    response.attachment !== false &&
                    response.attachment.url
                        ? response.attachment.url
                        : null);
                const newComment = {
                    id: response.message_id || response.id,
                    name: response.author_name || '',
                    comment: response.message || messageToSend,
                    date: response.create_date
                        ? formatCommentDate(response.create_date) +
                          ' ' +
                          formatCommentDateTime(response.create_date)
                        : new Date().toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                          }) +
                          ' ' +
                          new Date().toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit',
                          }) +
                          ' hs',
                    photo: response.author_picture || null,
                    sender: response.author_is_teacher ? 'user' : 'other',
                    author_is_teacher: response.author_is_teacher,
                    comment_type: response.comment_type || 'normal',
                    image: fileUrl,
                    attachments: {
                        fileUrl,
                        audioUrl: pickAttachmentUrl(resAtt.audio),
                        videoUrl: pickAttachmentUrl(resAtt.video),
                    },
                    sort_ms: response.create_date
                        ? getCommentSortMs(response.create_date)
                        : Date.now(),
                };
                setComments((prev) => [...prev, newComment]);
                setCommentChat('');
                clearChatMediaDraft();
                if (!conversationId && response.conversation_id) {
                    setConversationId(response.conversation_id);
                }
            }
        } catch (error) {
            console.error('Error al enviar el comentario:', error);
        } finally {
            setIsSendingMessage(false);
        }
    };

    const handleEditMessage = async (messageId, nextMessage, media = {}) => {
        const trimmedMessage = nextMessage.trim();

        if (!trimmedMessage) return false;

        setIsEditingMessage(true);

        try {
            const formData = new FormData();
            formData.append('message', trimmedMessage);
            const { file, audio, video } = media || {};
            if (file instanceof File) {
                formData.append('attachment', file);
            }
            if (audio instanceof Blob) {
                formData.append(
                    'file_audio',
                    audio,
                    audio instanceof File ? audio.name : 'audio_grabado.wav'
                );
            }
            if (video instanceof Blob) {
                formData.append(
                    'file_video',
                    video,
                    video instanceof File ? video.name : 'video_grabado.webm'
                );
            }

            const response = await editCommentResource(messageId, formData);

            if (response && !response.error) {
                const resAtt = response.attachments || {};
                const updatedFile =
                    pickAttachmentUrl(resAtt.file) ||
                    (response.attachment &&
                    response.attachment !== false &&
                    response.attachment.url
                        ? response.attachment.url
                        : null);
                setComments((prevComments) =>
                    prevComments.map((item) =>
                        item.id === messageId
                            ? {
                                  ...item,
                                  comment: trimmedMessage,
                                  image: updatedFile ?? item.image,
                                  attachments: {
                                      fileUrl:
                                          updatedFile ??
                                          item.attachments?.fileUrl,
                                      audioUrl:
                                          pickAttachmentUrl(resAtt.audio) ??
                                          item.attachments?.audioUrl,
                                      videoUrl:
                                          pickAttachmentUrl(resAtt.video) ??
                                          item.attachments?.videoUrl,
                                  },
                              }
                            : item
                    )
                );
                toast.success('Comentario actualizado con exito');
                return true;
            }

            toast.error(
                'No pudimos guardar los cambios. Intentalo nuevamente.'
            );
            return false;
        } catch (error) {
            console.error('Error al editar el comentario:', error);
            toast.error(
                'No pudimos guardar los cambios. Intentalo nuevamente.'
            );
            return false;
        } finally {
            setIsEditingMessage(false);
        }
    };

    const handleNote = (e) => {
        const { value } = e.target;
        const currentNote = parseInt(value, 10);
        if (currentNote < 0) setNote(100);
        else if (currentNote > 100) setNote(0);
        else setNote(currentNote);
    };

    const handleComment = (e) => {
        const { value } = e.target;
        setComment(value);
    };

    const handleSendAudio = (audio) => {
        setSound(audio);
    };

    const handleSendVideo = (vid) => {
        setVideo(vid);
    };

    const handleSendFile = (fileParam) => {
        setAttachmentFile(fileParam);
    };

    const correctedSubmission = !!page || hasPersistedCorrection;
    const isCorrectionMode = editorMode === 'correction';
    const initialGrade = correctedSubmission
        ? correctionWithCheck
            ? 100
            : Number(grade || 0)
        : 0;
    const initialRedo = Boolean(isRedo);
    const initialCheck = correctionWithCheck ? hasPersistedCorrection : false;
    const initialCorrectionComment = correctedSubmission
        ? String(savedCorrection.comment || '').trim()
        : '';
    const hasGradeChange =
        !correctionWithCheck && Number(note || 0) !== initialGrade;
    const hasRedoChange = Boolean(redo) !== initialRedo;
    const hasCheckChange =
        correctionWithCheck && Boolean(check) !== Boolean(initialCheck);
    const hasCorrectionCommentChange =
        correctedSubmission &&
        comment.trim() !== initialCorrectionComment.trim();
    const hasCorrectionAttachments = Boolean(sound || video || attachmentFile);
    const hasCorrectionChanges =
        hasGradeChange ||
        hasRedoChange ||
        hasCheckChange ||
        hasCorrectionCommentChange ||
        (!correctedSubmission && hasCorrectionAttachments);
    const hasCommentDraft = !!commentChat.trim();
    const isCorrectionDisabled = isCorrectionMode
        ? correctedSubmission
            ? !hasCorrectionChanges
            : correctionWithCheck
              ? !hasCorrectionChanges
              : redo
                ? false
                : !note && !comment.trim() && !hasCorrectionAttachments
        : true;

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const shouldSendCorrectionComment =
                !correctedSubmission || hasCorrectionCommentChange;
            const body = {
                redo: redo,
                grade: correctionWithCheck ? 100 : note,
                professor_id: user.repo_id,
                submission_id: idTask,
                from_table: false,
            };
            if (shouldSendCorrectionComment) {
                body.comment = comment;
            }

            let request;

            if (correctedSubmission) {
                request = await setTaskByStudentEdit(body);
            } else {
                const formData = new FormData();
                formData.append('submission_id', body.submission_id);
                formData.append('professor_id', body.professor_id);
                formData.append('comment', body.comment || '');
                formData.append('grade', body.grade);
                formData.append('redo', body.redo);
                formData.append('from_table', body.from_table);

                if (sound) {
                    formData.append('file_audio', sound);
                }
                if (video) {
                    formData.append('file_video', video);
                }
                if (attachmentFile) {
                    formData.append('file_image', attachmentFile);
                }

                request = await setTaskByStudentWithFiles(formData);
            }

            if (request?.error) {
                throw new Error(request?.error);
            }

            openSnackbar('¡Corrección exitosa!');
            setPage(true);
            setSavedCorrection({
                note: correctionWithCheck ? 100 : Number(note || 0),
                redo: Boolean(redo),
                check: Boolean(check),
                comment: comment,
            });
            setSound(null);
            setVideo(null);
            setAttachmentFile(null);
            setLoading(false);
            await loadConversation();
            return true;
        } catch (error) {
            openSnackbar('¡No se pudo corregir el trabajo!');
            setLoading(false);
            return false;
        }
    };

    const handleRedo = (e) => {
        setRedo(e.target.checked);
    };

    const handleEdit = () => {
        if (editorMode === 'correction') {
            setNote(savedCorrection.note);
            setRedo(savedCorrection.redo);
            setCheck(savedCorrection.check);
            setComment(savedCorrection.comment);
            setSound(null);
            setVideo(null);
            setAttachmentFile(null);
            setEditorMode('idle');
            return;
        }

        setEditorMode('correction');
    };

    const handleSaveCorrection = async () => {
        const success = await handleSubmit();
        if (success) {
            setEditorMode('idle');
        }
    };

    const handleNavigate = () => {
        const url = `../../profesor/alumno/${student?.partner_id}`;
        window.open(url, '_blank');
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <HistorySubmissionStudent
                history={history}
                open={open}
                handleClose={handleClose}
                idAssignment={idAssignment}
            />
            <TaskWrapper>
                <StudentTaskWrapper>
                    <StudentTaskHeader
                        correctedSubmission={correctedSubmission}
                        isRedo={isRedo}
                        redo={redo}
                        handleNavigate={handleNavigate}
                        old={old}
                        note={note}
                        history={history}
                        idAssignment={idAssignment}
                        grade={grade}
                        correctionWithCheck={correctionWithCheck}
                        resourceId={resourceId}
                        studentId={studentId}
                        subjectId={subjectId}
                        learningObject={learningObject}
                        teacherId={user?.repo_id}
                        submissionId={submissionId}
                        programId={programId}
                    />
                    {/* <DownloadActivity>
                        <p>
                            Si no puedes visualizar la actividad
                            <span> puedes descargarla en tu ordenador.</span>
                        </p>
                        <CustomButtonA
                            target="_blank"
                            href={isFile?.url}
                            download
                        >
                            <DownloadIcon />
                            Descargar actividad
                        </CustomButtonA>
                    </DownloadActivity> */}
                    <Body>
                        <Content>
                            <WrapperTitle>
                                <TitleBlock>
                                    <Text
                                        fontSize="16px"
                                        color="#222222"
                                        fontWeight="bold"
                                    >
                                        Calificación
                                    </Text>{' '}
                                </TitleBlock>
                                <ActionsBlock>
                                    <CustomButton onClick={handleOpen}>
                                        Ver historial de entregas
                                    </CustomButton>
                                </ActionsBlock>
                            </WrapperTitle>
                            <CardCorrectTask
                                createDate={createDate}
                                formatDate={formatDate}
                                correctionDate={formatCorrectedDate}
                                studentComment={studentComment}
                                correctionWithCheck={correctionWithCheck}
                                check={check}
                                setCheck={setCheck}
                                correctedSubmission={correctedSubmission}
                                valuesCorrection={valuesCorrection}
                                note={note}
                                redo={redo}
                                handleNote={handleNote}
                                handleRedo={handleRedo}
                                handleToggleEdit={handleEdit}
                                old={old}
                                order={order}
                                isEdit={isCorrectionMode}
                                isCorrectionSaving={loading}
                            />
                        </Content>
                        <Content>
                            {!old ? (
                                isCorrectionMode && (
                                    <CommentsSection
                                        comment={comment}
                                        activeRol={activeRol}
                                        handleComment={handleComment}
                                        loading={loading}
                                        handlePrimarySend={handleSaveCorrection}
                                        isPrimaryDisabled={
                                            loading || isCorrectionDisabled
                                        }
                                        submitLabel={'Guardar'}
                                        placeholder={
                                            correctedSubmission
                                                ? 'Explica qué cambió en la calificación o qué debe mejorar.'
                                                : 'Escribe un comentario sobre esta entrega.'
                                        }
                                        showTextarea={!correctedSubmission}
                                        onCancel={handleEdit}
                                        cancelLabel={
                                            correctedSubmission
                                                ? 'Cancelar'
                                                : 'Cancelar'
                                        }
                                        showCancel={correctedSubmission}
                                        showAttachmentPickers={
                                            !correctedSubmission
                                        }
                                        handleSendAudio={handleSendAudio}
                                        handleSendVideo={handleSendVideo}
                                        handleSendFile={handleSendFile}
                                        teacherFiles={teacherFiles}
                                        idTask={idTask}
                                    />
                                )
                            ) : (
                                <InfoOldSection
                                    valuesCorrection={valuesCorrection}
                                    studentComment={studentComment}
                                    teacherFiles={teacherFiles}
                                />
                            )}
                        </Content>

                        <>
                            <WrapperTitle>
                                <TitleBlock>
                                    <Text
                                        fontSize="16px"
                                        color="#222222"
                                        fontWeight="bold"
                                    >
                                        Comentarios sobre la entrega
                                    </Text>
                                </TitleBlock>
                            </WrapperTitle>
                            <Content pd $chat>
                                {commentsLoading ? (
                                    <SimpleLoading />
                                ) : (
                                    <CommentsChat
                                        comments={comments}
                                        composerVariant="media"
                                        handleSendAudio={handleChatSendAudio}
                                        handleSendVideo={handleChatSendVideo}
                                        handleSendFile={handleChatSendFile}
                                        mediaPickerResetKey={chatMediaReset}
                                        fileInputId="speedgrader-chat-attachment-file"
                                        handleSendMessage={handleSendComment}
                                        comment={commentChat}
                                        setComment={setCommentChat}
                                        isLoading={isSendingMessage}
                                        disabledWhenEmpty={false}
                                        emptyConversationMessage="Todavía no hay comentarios. Puedes iniciar la conversación con el alumno."
                                        hideComposer={false}
                                        inputPlaceholder="Escribe un comentario sobre esta entrega."
                                        sendButtonLabel="Enviar"
                                        canEditComment={(item) =>
                                            item.author_is_teacher === true &&
                                            item.is_legacy !== true
                                        }
                                        onEditMessage={handleEditMessage}
                                        isEditingMessage={isEditingMessage}
                                    />
                                )}
                            </Content>
                        </>
                    </Body>
                </StudentTaskWrapper>
            </TaskWrapper>
        </>
    );
};

export default StudentTask;

const TaskWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    flex: 1;
    height: calc(100% - 61px);

    @media (max-width: 1200px) {
        border-radius: 20px;
    }
`;

const StudentTaskWrapper = styled(Card)`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    min-width: 0;
    gap: 1.5rem;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-color: #e9e9e9 transparent;
    @media (max-width: 1200px) {
        border-radius: 20px;
    }
`;

const Body = styled.div`
    padding: 0 1.5rem 1rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    max-width: 100%;
    flex-shrink: 0;
    overflow-x: hidden;
    padding-top: ${(p) => (p.pd ? '1rem' : '0')};
    border-top: ${(p) => (p.pd ? '1px solid #f1f1f1' : 'none')};
    min-height: ${(p) => (p.$chat ? '360px' : 'auto')};
`;

const CustomButton = styled.button`
    color: #616161;
    font-size: 1rem;
    transition: all 0.3s ease-in-out;
    cursor: pointer;

    :hover {
        color: #b31d15;
    }
`;

const WrapperTitle = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ActionsBlock = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-left: auto;

    @media (max-width: 768px) {
        margin-left: 0;
    }
`;
